import { state } from './state.js';
import * as API from './api.js';
import * as UI from './ui.js';
import { handleHistorySelect } from './history_actions.js';
import { showToast } from './ui/toast.js';

let scrollAttached = false;
let fillAttached = false;

function normalizeHistoryResponse(data, limit) {
    const normalizeRetentionMeta = (retention) => {
        if (!retention || typeof retention !== 'object') return null;
        const limitVal = Number(retention.recent_mosaics_limit);
        const usedVal = Number(retention.used);
        const remainingVal = Number(retention.remaining);
        const daysVal = Number(retention.retention_days);
        const warningThresholdVal = Number(retention.soft_warning_threshold);
        const expiringSoonWindowVal = Number(retention.expiring_soon_window_s);
        return {
            recent_mosaics_limit: Number.isFinite(limitVal) && limitVal > 0 ? Math.floor(limitVal) : null,
            used: Number.isFinite(usedVal) ? Math.max(0, Math.floor(usedVal)) : null,
            remaining: Number.isFinite(remainingVal) ? Math.max(0, Math.floor(remainingVal)) : null,
            retention_days: Number.isFinite(daysVal) && daysVal > 0 ? Math.floor(daysVal) : null,
            soft_warning_threshold: Number.isFinite(warningThresholdVal) ? Math.floor(warningThresholdVal) : null,
            expiring_soon_window_s: Number.isFinite(expiringSoonWindowVal) && expiringSoonWindowVal > 0
                ? Math.floor(expiringSoonWindowVal)
                : null,
            limit_reached: Boolean(retention.limit_reached),
        };
    };

    if (Array.isArray(data)) {
        return {
            items: data,
            next_cursor: null,
            total: null,
            lifetime_total: null,
            has_more: data.length >= limit,
            retention: null,
        };
    }
    if (data && Array.isArray(data.items)) {
        const hasMore = typeof data.has_more === 'boolean'
            ? data.has_more
            : data.items.length >= limit;
        const totalVal = Number(data.total);
        const lifetimeTotalVal = Number(data.lifetime_total);
        return {
            items: data.items,
            next_cursor: data.next_cursor || null,
            total: Number.isFinite(totalVal) ? totalVal : null,
            lifetime_total: Number.isFinite(lifetimeTotalVal) ? lifetimeTotalVal : null,
            has_more: hasMore,
            retention: normalizeRetentionMeta(data.retention),
        };
    }
    return {
        items: [],
        next_cursor: null,
        total: null,
        lifetime_total: null,
        has_more: false,
        retention: null,
    };
}

export function applyHistoryRetentionMeta(retention) {
    if (!retention || typeof retention !== 'object') {
        state.historyRetention = null;
        maybeShowRecentMosaicsSoftWarning();
        return null;
    }
    const normalized = normalizeHistoryResponse({ items: [], retention }, 1).retention;
    state.historyRetention = normalized;
    maybeShowRecentMosaicsSoftWarning();
    return normalized;
}

function maybeShowRecentMosaicsSoftWarning() {
    const retention = state.historyRetention || null;
    const limit = Number(retention && retention.recent_mosaics_limit);
    const used = Number(retention && retention.used);
    const remaining = Number(retention && retention.remaining);
    if (!Number.isFinite(limit) || limit <= 0 || !Number.isFinite(remaining)) {
        state.historySoftWarningKey = '';
        return;
    }
    if (remaining !== 1) {
        state.historySoftWarningKey = '';
        return;
    }
    const warnKey = `${limit}:${used}:${remaining}`;
    if (state.historySoftWarningKey === warnKey) return;
    state.historySoftWarningKey = warnKey;
    showToast('Recent mosaics', `You have 1 slot left (${used}/${limit} used).`, 4200);
    API.logEvent('recent_mosaics_soft_warning_shown', {
        used,
        limit,
        remaining,
    }).catch(() => {});
}

export function attachHistoryScroll(loadPage) {
    if (scrollAttached) return;
    const strip = document.getElementById('historyStrip');
    if (!strip) return;
    strip.addEventListener('scroll', () => {
        const nearEnd = (strip.scrollLeft + strip.clientWidth) >= (strip.scrollWidth - 80);
        if (nearEnd) Promise.resolve(loadPage()).catch(() => {});
    });
    const loadMore = document.getElementById('historyLoadMore');
    if (loadMore) {
        loadMore.addEventListener('click', (e) => {
            e.preventDefault();
            Promise.resolve(loadPage()).catch(() => {});
        });
    }
    if (!fillAttached) {
        fillAttached = true;
        document.addEventListener('history:fill', () => {
            if (state.historyLoading || !state.historyHasMore) return;
            const loaded = (state.history || []).length;
            const needed = state.historyLimit - loaded;
            if (needed <= 0) return;
            Promise.resolve(loadPage(needed)).catch(() => {});
        });
    }
    scrollAttached = true;
}

export function resetHistoryState() {
    state.history = [];
    state.historyOffset = 0;
    state.historyHasMore = true;
    state.historyLoading = false;
    state.historyCursor = null;
    state.historyTotal = null;
    state.historyLifetimeTotal = null;
    state.historyRetention = null;
    state.historySoftWarningKey = '';
    state.selectedHistoryJob = null;
}

export async function reloadHistoryFirstPage(limitOverride = null) {
    const requestedLimit = Math.max(1, Number(limitOverride || state.historyLimit));
    state.historyLoading = false;
    state.historyHasMore = true;
    state.historyCursor = null;
    state.historyOffset = 0;
    await loadHistoryPage(requestedLimit);
}

export async function loadHistoryPage(limitOverride = null) {
    if (state.historyLoading || !state.historyHasMore) return;
    state.historyLoading = true;
    try {
        const isFirstPage = state.historyOffset === 0;
        const requestedLimit = Math.max(1, Number(limitOverride || state.historyLimit));
        const data = await API.getHistory(requestedLimit, state.historyCursor);
        const normalized = normalizeHistoryResponse(data, requestedLimit);
        const jobs = normalized.items || [];

        if (isFirstPage && String(state.historyBootStatus || '') === 'loading') {
            state.historyBootStatus = 'loaded';
        }

        if (isFirstPage) {
            state.history = jobs;
        } else {
            state.history = state.history.concat(jobs);
        }

        state.historyOffset = state.history.length;
        state.historyCursor = normalized.next_cursor || null;
        state.historyHasMore = Boolean(normalized.has_more);
        applyHistoryRetentionMeta(normalized.retention);

        if (Number.isFinite(normalized.total)) {
            state.historyTotal = normalized.total;
            if (state.history.length >= normalized.total) {
                state.historyHasMore = false;
            }
        }
        if (Number.isFinite(normalized.lifetime_total)) {
            state.historyLifetimeTotal = normalized.lifetime_total;
        }

        UI.renderHistory(jobs, handleHistorySelect, { reset: isFirstPage });
        UI.updateHistoryMeta();
    } finally {
        state.historyLoading = false;
    }
}

export function upsertHistoryItem(job, opts = {}) {
    if (!job || !job.job_id) return;
    const select = Boolean(opts.select);
    const adjustTotals = opts.adjustTotals !== false;
    const existingIdx = (state.history || []).findIndex((j) => j.job_id === job.job_id);
    if (existingIdx >= 0) {
        state.history.splice(existingIdx, 1);
    }
    state.history.unshift(job);

    if (state.historyOffset > 0 && existingIdx < 0) {
        state.historyOffset += 1;
    }

    if (adjustTotals && Number.isFinite(state.historyTotal) && existingIdx < 0) {
        state.historyTotal = Math.max(0, Number(state.historyTotal) + 1);
    }
    if (adjustTotals && Number.isFinite(state.historyLifetimeTotal) && existingIdx < 0) {
        state.historyLifetimeTotal = Math.max(0, Number(state.historyLifetimeTotal) + 1);
    }

    const maxKeep = Math.max(Number(state.historyOffset || 0), Number(state.historyLimit || 40));
    if (state.history.length > maxKeep) {
        state.history = state.history.slice(0, maxKeep);
    }

    UI.renderHistory([job], handleHistorySelect, {
        prepend: true,
        selectJobId: select ? job.job_id : null,
    });
    UI.updateHistoryMeta();
    if (select) {
        const strip = document.getElementById('historyStrip');
        if (strip) {
            strip.scrollLeft = 0;
        }
    }
}
